import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { PerformanceEvaluation } from '../../prompts/evaluation-performance-prompt';

@Component({
  selector: 'dc-feedback-evaluation',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './feedback-evaluation.component.html',
  styleUrls: ['./feedback-evaluation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackEvaluationComponent implements OnInit {
  ref = inject(DynamicDialogRef);
  config = inject(DynamicDialogConfig);

  public evaluationData!: PerformanceEvaluation;

  constructor() {}

  ngOnInit(): void {
    if (this.config.data) {
      this.evaluationData = this.config.data;
    } else {
      this.evaluationData = this.getDummyEvaluationData();
    }
  }

  private getDummyEvaluationData(): PerformanceEvaluation {
    return {
      overall_score: {
        rating: 85,
        level: 4,
      },
      assessments: {
        grammar: {
          score: 90,
          level: 5,
          feedback: 'Excellent grammar, with very few mistakes. Your sentence structures are complex and accurate.',
          suggestions: ['Review the use of subjunctive mood for hypothetical situations.'],
        },
        vocabulary: {
          score: 88,
          level: 4,
          feedback: 'You have a wide range of vocabulary and use it appropriately. You could try to incorporate more idiomatic expressions.',
          suggestions: ['Learn phrasal verbs related to business.', 'Practice using synonyms for common words.'],
        },
        communication_effectiveness: {
          score: 82,
          level: 'Advanced',
          feedback: 'Your communication is clear and effective. You are able to convey your ideas with precision.',
          suggestions: ['Work on your turn-taking skills in fast-paced conversations.'],
        },
        pragmatic_competence: {
          score: 80,
          level: 'Advanced',
          feedback: 'You show good awareness of social and cultural norms in communication.',
          suggestions: ['Study how humor is used in professional contexts.'],
        },
      },
      conversation_analysis: {
        dominant_register: 'formal',
        conversation_type: 'professional',
        key_observations: ['You maintained a professional tone throughout the conversation.', 'You used appropriate language for the context.'],
        notable_patterns: ['Consistent use of complex sentences.', 'Tendency to use direct questions.'],
      },
      actionable_feedback: {
        immediate_focus_areas: ['Using more varied intonation.'],
        practice_recommendations: [
          'Record yourself speaking and listen for intonation patterns.',
          'Engage in role-playing scenarios that require different emotional expressions.',
        ],
        next_steps: ['Have a conversation with a native speaker focusing on expressing excitement and disappointment.'],
      },
    };
  }

  closeDialog() {
    this.ref.close();
  }
}
